import { useCallback } from 'react';
import { useCollectionsStore, setCollections } from '../store/collectionsStore';
import { useWorkspaceStore, setDeviceLocalCollections } from '../store/workspaceStore';
import { useAuthStore } from '../store/authStore';
import { collectionService } from '../services/collectionService';
import { isBackendWorkspace, isCloudRestWorkspace } from '../utils/workspaceStorage';
import { stripVariableRowIds } from '../utils/collectionTabState';
import { getFolderById } from '../utils/collectionFolders';
import { restoreFolderKeys, storedKeysFor, withFolderPageFields } from '../utils/folderPageFields';
import { syncLinkedLocalCopySettings } from '../utils/linkedLocalCopySync';
import { loadAllDeviceLocalCollections } from '../utils/deviceLocalCollections';

/**
 * The one edit gate for a folder, used by the folder page AND the save below
 * (tester F5: the save itself must refuse, not only the UI). Same rule as the
 * collection editor (`can_edit`) plus App's read-only team workspace test.
 */
export function isFolderReadOnly(collection, workspace, isAuthenticated) {
  if (!collection) return true;
  if (collection.can_edit === false) return true;
  return (
    isBackendWorkspace(workspace, isAuthenticated) &&
    workspace?.type === 'team' &&
    workspace?.can_edit === false
  );
}

export class FolderReadOnlyError extends Error {
  constructor() {
    super('You cannot edit this folder.');
    this.name = 'FolderReadOnlyError';
  }
}

const sameId = (a, b) => String(a) === String(b);

/**
 * Persist one folder's page fields (docs, auth, variables, scripts — see
 * utils/folderPageFields). Only the keys present in `fields` are written.
 *
 * Folder data lives in `collection.settings.folders[i]`, so a save is a
 * collection-SETTINGS save — the same call the collection editor makes — and
 * never a sidebar-tree save: `PUT /sidebar-layout` rebuilds folders from
 * `{id, name, parent_id, sort_order}` and would not carry them.
 *
 * Order (review N1): the store is updated FIRST, from its CURRENT state,
 * replacing only this folder's touched keys — a rename or another settings
 * change that lands while the request is in flight is kept, not reverted. The
 * cloud PUT body is then read FRESH from the store (the pattern
 * `duplicateFolder` documents). If the PUT fails, only those keys of this
 * folder are rolled back, again against the current state.
 *
 *   - cloud REST: `updateCollection({ settings })`, then the linked local copy.
 *   - local / git / sync-engine: the store IS the persist (the snapshot effect
 *     writes it; see mcp/collectionsDb.js for why).
 *
 * Throws on refusal (read-only), a missing collection/folder, or a failed PUT.
 */
export async function saveFolderFields({ collectionId, folderId, fields }) {
  const { currentWorkspace, deviceLocalCollections, workspaces } = useWorkspaceStore.getState();
  const isAuthenticated = useAuthStore.getState().isAuthenticated;
  const collection = (useCollectionsStore.getState().collections || [])
    .find((c) => sameId(c.id, collectionId));
  if (!collection) throw new Error('Collection not found');
  if (isFolderReadOnly(collection, currentWorkspace, isAuthenticated)) {
    throw new FolderReadOnlyError();
  }
  const folder = getFolderById(collection, folderId);
  if (!folder) throw new Error('Folder not found');
  const patch = { ...fields };
  if ('variables' in patch) patch.variables = stripVariableRowIds(patch.variables);
  const touched = storedKeysFor(patch);
  const previous = { ...folder };

  setCollections((prev) =>
    (prev || []).map((c) => (sameId(c.id, collectionId) ? withFolderPageFields(c, folderId, patch) : c))
  );

  if (!isCloudRestWorkspace(currentWorkspace, isAuthenticated)) return true;

  const fresh = (useCollectionsStore.getState().collections || []).find((c) => sameId(c.id, collectionId));
  try {
    await collectionService.updateCollection(collectionId, { settings: fresh.settings });
  } catch (err) {
    setCollections((prev) =>
      (prev || []).map((c) => (sameId(c.id, collectionId) ? restoreFolderKeys(c, folderId, previous, touched) : c))
    );
    throw err;
  }
  try {
    await syncLinkedLocalCopySettings({
      collectionId,
      collection: fresh,
      nextSettings: fresh.settings,
      deviceLocalCollections,
      workspaces,
      reloadDeviceLocalCollections: async () => {
        setDeviceLocalCollections(await loadAllDeviceLocalCollections());
      },
    });
  } catch (err) {
    // The server has the change; a stale device-local mirror is re-synced by
    // the next save or refresh. Not a reason to report the save as failed.
    console.error('Folder save: linked local copy not updated:', err);
  }
  return true;
}

/** Hook form, for components that want a stable callback. */
export function useFolderSave() {
  return useCallback((args) => saveFolderFields(args), []);
}

export default useFolderSave;
